import { useEffect, useState, forwardRef } from "react";
import { connect } from "react-redux";
import { DateTime } from "luxon";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { reminderTimes } from "Constants";
import { updateReminderAlert } from "../redux/index";
import NotificationImportantTwoToneIcon from "@mui/icons-material/NotificationImportantTwoTone";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SomuraSwitchField from "Components/SomuraSwitchField";
import SomuraSelectField from "Components/SomuraSelectField";
import Slide from "@mui/material/Slide";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="right" ref={ref} {...props} />;
});

const mapStateToProps = (state) => {
  return {
    reminderIds: state.general.Stats.alertIds,
    reminders: state.reminders.Reminders,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateReminderAlert: (values) => dispatch(updateReminderAlert(values)),
  };
};

const Reminder = ({
  closeReminder,
  reminderIds,
  reminders,
  updateReminderAlert,
}) => {
  const { t } = useTranslation();

  const [state, setState] = useState({
    title: "",
    date: "",
    time: "",
    alertTimeId: 0,
    alertDateTime: DateTime.now().toFormat("yyyy-MM-dd HH:ii:ss"),
    categoryName: "",
    noRemind: false,
    isAnniversary: false,
    anniversaryYear: 0,
    isWholeDay: true,
  });

  var alertTimeOptions = reminderTimes.map((RT) => ({
    label: "In " + RT.number + " " + t(RT.text) + (t(RT.text) === "Tage" ? "n" : ""),
    value: RT.id,
  }));
  alertTimeOptions.unshift({
    label: t("When somura starts"),
    value: -2,
  });

  const handleOkClick = () => {
    updateReminderAlert({
      ...state,
      enableAlert: state.noRemind ? 0 : 1,
      ...createAlertDateTime(state.alertTimeId),
    });
    closeReminder()
  }

  const createAlertDateTime = (alertTimeId) => {
    const reminderTime = reminderTimes.find((RT) => RT.id === alertTimeId);
    const alertDateTime = DateTime.now().plus(reminderTime.interval)
      .toFormat("yyyy-MM-dd HH:mm:ss");
    return {
      alertTimeId: alertTimeId,
      alertDateTime: alertDateTime,
    };
  };

  const handleSwitchChange = (event) => {
    const value = event.target.checked;
    setState({ ...state, noRemind: value });
  };

  const handleAlertChange = (event) => {
    var value = event.target.value;
    setState({ ...state, alertTimeId: value, ...createAlertDateTime(value) });
  };

  useEffect(() => {
    if (reminderIds?.length > 0 && Object.keys(reminders).length > 0) {
      const reminder = reminders?.find(
        (R) => parseInt(R.id) === parseInt(reminderIds[0]),
      );
      setState({
        id: reminder.id,
        title: reminder.title,
        date: reminder.date,
        time: reminder.time,
        alertTimeId: 1,
        alertDateTime: reminder.alertDateTime,
        categoryName: reminder.categoryName,
        noRemind: false,
        isAnniversary: reminder.isAnniversary,
        anniversaryYear: reminder.anniversaryYear,
        isWholeDay: reminder.isWholeDay,
      });
    }
    //eslint-disable-next-line
  }, [reminderIds]);

  return (
    <div>
      <Dialog
        slots={{
          transition: Transition,
        }}
        sx={{
          "& .MuiDialog-paper": {
            position: "fixed",
            top: 0,
            right: 0,
            margin: "20px",
          },
        }}
        open
        onKeyUp={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            closeReminder();
          }
          if (e.key === "Escape") {
            closeReminder();
          }
        }}
        onClose={closeReminder}
      >
        <DialogTitle
          sx={{
            color: "white",
            backgroundColor: "somura",
            padding: "6px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontWeight: "100",
            }}
          >
            <NotificationImportantTwoToneIcon />
            {t("Appointment reminder") + " "}
            <Box sx={{ fontSize: "0.8rem" }}>
              {state.alertDateTime.substring(11, 16)}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "background.scrollBar",
            paddingBottom: 0,
          }}
        >
          <Box
            sx={{
              color: "text.calendars",
              fontSize: "1.2rem",
              fontWeight: "700",
              textAlign: "center",
            }}
          >
            {(state.categoryName !== "" ? state.categoryName + ": " : "") + state.title}
          </Box>
          <Box
            sx={{
              color: "text.reminder",
              fontSize: "0.9rem",
              textAlign: "center",
            }}
          >
            {state.isAnniversary && state.anniversaryYear > 0 && (
              <Box>
                {DateTime.now().year -
                  state.anniversaryYear +
                  ". " +
                  state.categoryName}
              </Box>
            )}
            {DateTime.fromSQL(state.date).toLocaleString(DateTime.DATE_HUGE) +
              " " +
              (state.isWholeDay ? "" : state.time)}
          </Box>
          <SomuraSwitchField
            sx={{ color: "black" }}
            name="noRemind"
            label={t("Do not remind me again")}
            checked={state.noRemind}
            onChange={handleSwitchChange}
          />
          <SomuraSelectField
            color={"text.reminder"}
            label={t("Remind again")}
            labelBackgroundColor={"background.scrollBar"}
            name="alertTimeId"
            options={alertTimeOptions}
            value={
              state.alertTimeId === undefined || state.alertTimeId === null
                ? -1
                : state.alertTimeId
            }
            onChange={handleAlertChange}
            disabled={state.noRemind}
          />
          {!state.noRemind && (
            <Box
              sx={{ textAlign: "center", fontSize: "0.8rem", color: "black" }}
            >
              {DateTime.fromSQL(state.alertDateTime).toLocaleString(
                DateTime.DATETIME_FULL,
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            backgroundColor: "background.scrollBar",
          }}
        >
          <Button color="primary" variant="contained" onClick={handleOkClick}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Reminder);
